import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { FiLoader } from "react-icons/fi";
import { getApiEndpoint } from "../../config/amplifyOutputs";
import { ChatMessage } from "../../store/aiChatSlice";
import { normalizeAiMessageText } from "../../services/mcpService";
import { parseUpdateBlocks } from "../../utils/chatUpdates";
import AiChatMessages from "../../components/AiChatComponent/panelComponents/AiChatMessages";
import { ReactComponent as ErodeLogo } from "../../assets/images/erode-logo-black.svg";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";

// Lean, iframe-embeddable view of a shared agent-schedule result. Public (no Cognito auth) and
// stripped of the sign-in CTA. Self-refreshes on an interval so a live "scheduleRunner" share picks
// up new runs (the snapshot.json it serves is re-generated server-side each run).

const PUBLIC_API_NAME = "sharePublicApi";
const REFRESH_INTERVAL_MS = 60000; // 60s — matches the agreed live-embed cadence.
// Force a full (non-304) refetch at least this often so presigned image URLs (24h expiry)
// are re-minted long before they age out on an unchanged snapshot.
const ETAG_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const GONE_MESSAGE =
  "The content you are trying to view is no longer available. It is possible the original owner has turned off sharing.";

interface SnapshotKmlAttachment {
  filename: string;
  size: number;
  mimetype: string;
  uri: string;
}

interface SnapshotMessage {
  id: string;
  content: string;
  isUser: boolean;
  createdAt?: string;
  kmlAttachments?: SnapshotKmlAttachment[];
}

interface SharedSnapshot {
  title: string;
  conversationType?: string;
  scope?: string;
  createdAt?: string;
  messages: SnapshotMessage[];
}

type LoadState = "loading" | "success" | "gone";

// Resolve the public REST endpoint base URL from the bundled amplify config.
function getPublicApiBase(): string | null {
  return getApiEndpoint(PUBLIC_API_NAME);
}

const EmbedConversationPage: React.FC = () => {
  const { shareId } = useParams<{ shareId: string }>();
  const { capture } = usePostHogCapture();

  const [state, setState] = useState<LoadState>("loading");
  const [snapshot, setSnapshot] = useState<SharedSnapshot | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const viewedFiredRef = useRef(false);
  // Conditional-polling state: the last 200's ETag and when we stored it. A matching
  // If-None-Match lets the view Lambda answer 304 and the page skip the re-render.
  const etagRef = useRef<string | null>(null);
  const etagStoredAtRef = useRef(0);

  // Keep the embed out of search indexes (same rationale as the full share page).
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  // Fetch the public snapshot, then self-refresh on an interval. Interval refetches update silently
  // (no "loading" flash) so the live embed never blinks between runs.
  useEffect(() => {
    if (!shareId) {
      setState("gone");
      return;
    }

    let cancelled = false;

    const fetchSnapshot = async (isInitial: boolean) => {
      const base = getPublicApiBase();
      const url = base ? `${base}/share/${shareId}` : `/share/${shareId}`;
      try {
        // Expire the stored ETag periodically so an unchanged snapshot still gets a full
        // 200 now and then — that re-presigns the image URLs before their 24h expiry.
        if (etagRef.current && Date.now() - etagStoredAtRef.current > ETAG_MAX_AGE_MS) {
          etagRef.current = null;
        }
        // cache: "no-store" keeps the browser HTTP cache out of the manual conditional
        // exchange (otherwise it layers its own revalidation and behavior varies by browser).
        const res = await fetch(url, {
          cache: "no-store",
          headers: etagRef.current ? { "If-None-Match": etagRef.current } : undefined,
        });
        // 304: unchanged since the last 200 — keep the current render. Must be checked
        // before res.ok (a 304 is not "ok").
        if (res.status === 304) return;
        if (res.status === 410) {
          // Revoked (sharing turned off) — blank the embed even mid-session.
          if (!cancelled) setState("gone");
          return;
        }
        if (!res.ok) {
          // Only downgrade to "gone" on the initial load; a transient failure on a refresh
          // keeps the last good snapshot on screen.
          if (!cancelled && isInitial) setState("gone");
          return;
        }
        const data = (await res.json()) as SharedSnapshot;
        if (cancelled) return;
        etagRef.current = res.headers.get("ETag");
        etagStoredAtRef.current = Date.now();
        setSnapshot(data);
        setState("success");
      } catch (err) {
        console.error("[EmbedConversationPage] Failed to fetch shared result:", err);
        if (!cancelled && isInitial) setState("gone");
      }
    };

    fetchSnapshot(true);
    // Skip refreshes while the embed is hidden (background tab / off-screen iframe) to avoid
    // pointless requests; the next visible tick still picks up the latest run.
    const intervalId = window.setInterval(() => {
      if (document.visibilityState === "hidden") return;
      fetchSnapshot(false);
    }, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [shareId]);

  // Fire the viewed event once after the first successful load.
  useEffect(() => {
    if (state === "success" && !viewedFiredRef.current) {
      viewedFiredRef.current = true;
      capture(POSTHOG_EVENTS.SHARED_CONVERSATION_VIEWED, {
        share_id: shareId,
        conversation_type: snapshot?.conversationType,
        surface: "embed",
      });
    }
  }, [state, shareId, snapshot?.conversationType, capture]);

  // Map snapshot messages into the ChatMessage shape AiChatMessages expects.
  // Embedded agent results show only the AI responses, never the prompt that triggered them.
  const messages: ChatMessage[] = useMemo(() => {
    if (!snapshot?.messages) return [];
    return snapshot.messages.filter((m) => !m.isUser).map((m) => ({
      id: m.id,
      // Strip streamed <update> progress steps from AI messages — show only final answers.
      text: normalizeAiMessageText(parseUpdateBlocks(m.content).response, false),
      isUser: false,
      createdAt: m.createdAt,
      attachments: m.kmlAttachments?.length ? m.kmlAttachments : undefined,
    }));
  }, [snapshot]);

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-eai-primary-accent/10 via-white to-white text-gray-500">
        <FiLoader className="mr-2 h-5 w-5 animate-spin" /> Loading…
      </div>
    );
  }

  const isGone = state === "gone" || !snapshot;
  const sharedDate =
    !isGone && snapshot?.createdAt
      ? new Date(snapshot.createdAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gradient-to-b from-eai-primary-accent/10 via-white to-white">
      {/* Header: matches the public share page (logo left, byline right) — no interactive
          share/sign-in controls, since this view is embedded in an iframe. */}
      <header className="shrink-0 pb-3 pt-6">
        <div className="mx-auto flex max-w-6xl items-end justify-between gap-3 px-6 sm:px-8">
          <a
            href="https://erode.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0"
            aria-label="Erode — erode.ai"
          >
            <ErodeLogo className="h-7 w-auto" aria-label="Erode" />
          </a>
          <p className="min-w-0 truncate text-xs text-gray-400">
            Shared from Erode AI{sharedDate ? ` · ${sharedDate}` : ""}
          </p>
        </div>
      </header>

      {/* Body: a single fixed-height rounded card that fills the space between the pinned
          header and footer. The title + label stay pinned at the top of the card; only the
          response area below scrolls, so the card keeps a constant size inside the iframe. */}
      <main className="min-h-0 flex-1 overflow-hidden">
        <div className="mx-auto flex h-full w-full max-w-6xl flex-col px-4 pb-6 pt-3 sm:px-6">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 pb-4 shadow-md ring-1 ring-black/5">
            {isGone ? (
              <div className="flex flex-1 items-center justify-center p-6">
                <p className="px-4 text-center text-base text-gray-700">{GONE_MESSAGE}</p>
              </div>
            ) : (
              <>
                {/* Pinned card header — title + live-run label, always visible. */}
                <div className="shrink-0 border-b border-gray-200 px-4 pb-3 pt-4 sm:px-6 sm:pt-6">
                  {snapshot?.title && (
                    <h1 className="text-lg font-semibold text-gray-900">
                      {snapshot.title.replace(/^\[Scheduled\]\s*/i, "")}
                    </h1>
                  )}
                  <span className="mt-1 block text-xs font-medium uppercase tracking-wide text-gray-400">
                    Live Shared Agent Run
                  </span>
                </div>
                {/* Scrollable response — the only part that scrolls. The card's own pb-4
                    keeps the response off the bottom edge at every scroll position. */}
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pt-4 pb-4 sm:px-6">
                  {messages.length === 0 ? (
                    <p className="py-10 text-center text-sm text-gray-400">
                      This result has no content to display yet.
                    </p>
                  ) : (
                    <AiChatMessages
                      messages={messages}
                      isLoading={false}
                      isFetchingConversation={false}
                      messagesEndRef={messagesEndRef}
                      readOnly
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      <footer className="shrink-0 px-4 py-4 sm:px-6">
        <a
          href="https://erode.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="mx-auto flex max-w-6xl items-center justify-center gap-1.5 text-xs text-gray-400 transition-colors hover:text-gray-600"
        >
          <span>Powered by</span>
          <ErodeLogo className="h-3.5 w-auto opacity-60" aria-label="Erode" />
        </a>
      </footer>
    </div>
  );
};

export default EmbedConversationPage;
